import type { CollectedDataCategory, LegalTexts, SharedDataType, Subprocessor } from '~/modules/auth/legal/legal-types';
import { lazyNamed } from '~/utils/lazy-named';

export type LegalSubject = keyof typeof legalConfig;

/** Sections are declared statically so the legal page needs no DOM scanning. */
export const legalConfig = {
  privacy: {
    component: lazyNamed(() => import('~/modules/auth/legal/privacy-text'), 'PrivacyText'),
    label: 'c:privacy_policy',
    sections: [
      { id: 'overview', label: null },
      { id: 'introduction', label: 'Introduction' },
      { id: 'data-we-collect', label: 'Data we collect' },
      { id: 'how-we-use-data', label: 'How we use data' },
      { id: 'data-processing', label: 'Data processing' },
      { id: 'cookies', label: 'Cookies' },
      { id: 'data-retention', label: 'Data retention' },
      { id: 'security', label: 'Security' },
      { id: 'your-rights', label: 'Your rights' },
      { id: 'changes', label: 'Policy changes' },
      { id: 'subprocessors', label: 'Subprocessors' },
      { id: 'processed-data-types', label: 'Processed data' },
    ],
  },
  terms: {
    component: lazyNamed(() => import('~/modules/auth/legal/terms-text'), 'TermsText'),
    label: 'c:terms_of_use',
    sections: [
      { id: 'overview', label: null },
      { id: 'introduction', label: 'Introduction' },
      { id: 'agreement', label: 'Agreement' },
      { id: 'privacy', label: 'Privacy' },
      { id: 'accounts', label: 'Accounts' },
      { id: 'acceptable-use', label: 'Acceptable use' },
      { id: 'intellectual-property', label: 'Intellectual property' },
      { id: 'service-changes', label: 'Service changes' },
      { id: 'termination', label: 'Termination' },
      { id: 'disclaimers', label: 'Disclaimers' },
      { id: 'liability', label: 'Liability' },
      { id: 'indemnification', label: 'Indemnification' },
      { id: 'governing-law', label: 'Governing law' },
      { id: 'general', label: 'General' },
    ],
  },
} as const satisfies LegalTexts;

export const legalSubjects = Object.keys(legalConfig) as [LegalSubject, ...LegalSubject[]];

/** Default legal subject used when no or an unknown subject is requested. */
export const defaultLegalSubject = legalSubjects[0];

/** Personal data categories collected by this app; update when data collection changes. */
export const collectedData: CollectedDataCategory[] = [
  {
    label: 'Account information',
    description: 'When you create an account we collect:',
    items: ['Name', 'Email address', 'Authentication credentials'],
  },
  {
    label: 'User-generated content',
    description: 'Content you or your organization members create, upload or share through the Service, including:',
    items: ['Tasks, projects and workspace data', 'Attachments (images, files, videos)', 'Profile information (name, avatar, biography)'],
  },
  {
    label: 'Automatically collected data',
    description: 'When you interact with the Service we automatically collect:',
    items: ['Browser type and version', 'Anonymized count of pages and data requested', 'Anonymized IP addresses (for rate limiting and security)'],
  },
];

/** Subprocessors handling personal data; `optional: true` marks services an app can filter out. */
export const subprocessors: Subprocessor[] = [
  {
    slug: 'scaleway',
    name: 'Scaleway',
    legalName: 'Scaleway SAS',
    website: 'https://www.scaleway.com/',
    servicesProvided: ['Server', 'Database', 'Object storage'],
    processingActivities: ['Hosting and storing customer data', 'Serving and transferring files'],
    categoriesOfPersonalData: ['Contact data', 'Identifiers', 'Customer content and files'],
    purposes: ['Service hosting and data storage', 'Backup and availability', 'Content delivery'],
    country: 'France',
    dpa: { signed: true, effectiveDate: '2022-07-01', url: 'https://www.scaleway.com/en/terms-and-conditions/data-processing-agreement/' },
  },
  {
    slug: 'brevo',
    name: 'Brevo',
    legalName: 'Brevo',
    website: 'https://www.brevo.com/',
    servicesProvided: ['Transactional email delivery'],
    processingActivities: ['Sending verification, notification and system emails'],
    categoriesOfPersonalData: ['Email addresses', 'Names', 'Email content'],
    purposes: ['Transactional email delivery', 'Email deliverability tracking'],
    country: 'France',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://www.brevo.com/legal/termsofuse/#data-processing-agreement' },
  },
  {
    slug: 'transloadit',
    name: 'Transloadit',
    legalName: 'Transloadit Ltd.',
    website: 'https://transloadit.com/',
    servicesProvided: ['File processing', 'Image and video encoding'],
    processingActivities: ['Transforming, validating and encoding uploaded files'],
    categoriesOfPersonalData: ['Uploaded files', 'File metadata'],
    purposes: ['File transformation and validation', 'Image processing'],
    country: 'Germany',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://transloadit.com/legal/dpa/' },
    optional: true,
  },
  {
    slug: 'gleap',
    name: 'Gleap',
    legalName: 'Gleap GmbH',
    website: 'https://gleap.io/',
    servicesProvided: ['Customer support', 'Bug reporting'],
    processingActivities: ['Collecting and storing support messages, feedback and browser context'],
    categoriesOfPersonalData: ['Email addresses', 'Support messages', 'Browser and page context'],
    purposes: ['Customer support', 'Bug reporting and feedback'],
    country: 'Austria',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://gleap.io/privacy-policy/' },
    optional: true,
  },
];

/** Shared data types describing personal data flows; `optional: true` marks filterable entries. */
export const sharedDataTypes: SharedDataType[] = [
  {
    slug: 'user_profile',
    name: 'User profile data',
    purpose: 'Store user identity and preferences for authentication and personalization.',
    legalBasis: 'contract',
    dataCategories: ['name', 'email address', 'avatar', 'language preference'],
    dataSubjects: ['end users'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: 'Retained until account deletion, then permanently removed within 90 days.',
  },
  {
    slug: 'auth_sessions',
    name: 'Authentication sessions',
    purpose: 'Maintain authenticated sessions and manage secure access.',
    legalBasis: 'contract',
    dataCategories: ['session tokens', 'OAuth tokens', 'refresh tokens'],
    dataSubjects: ['end users'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: 'Tokens expire on logout or after 30 days of inactivity.',
  },
  {
    slug: 'organization_membership',
    name: 'Organization membership',
    purpose: 'Manage roles, permissions and membership within organizations.',
    legalBasis: 'contract',
    dataCategories: ['membership role', 'permissions', 'join date'],
    dataSubjects: ['organization members'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: 'Retained until membership is removed or organization is deleted.',
  },
  {
    slug: 'user_uploads',
    name: 'User file uploads',
    purpose: 'Store and deliver user-uploaded files within the workspace.',
    legalBasis: 'contract',
    dataCategories: ['user files', 'file metadata (names, timestamps, owner ID)'],
    dataSubjects: ['end users', 'organization members'],
    storageLocation: 'Scaleway Object Storage (EU)',
    retentionPeriod: 'Files retained until deleted by the user or up to 90 days after account deletion.',
  },
  {
    slug: 'transactional_emails',
    name: 'Transactional emails',
    purpose: 'Send system emails such as verification and notifications.',
    legalBasis: 'contract',
    dataCategories: ['email address', 'email content', 'delivery status'],
    dataSubjects: ['end users'],
    storageLocation: 'Brevo (EU)',
    retentionPeriod: 'Email logs retained for 30 days.',
  },
  {
    slug: 'error_data',
    name: 'Error, performance and session data',
    purpose: 'Collect application errors, performance metrics and masked session replays for reliability.',
    legalBasis: 'legitimate interest',
    dataCategories: ['error stacktraces', 'browser metadata', 'masked session recordings', 'anonymized IP addresses'],
    dataSubjects: ['end users'],
    storageLocation: 'Maple.dev (EU/US)',
    retentionPeriod: 'Retained per Maple plan settings (default 30 days).',
    optional: true,
  },
];
